import { LEVEL_TREE, LEVEL_MAP, LevelNode, isDemoCapped } from '@/map/LevelTree';
import { isDevMode } from '@/systems/DevConsole';
import { EventBus, GameEvents } from '@/events/EventBus';
import { ComponentType, COMPONENT_DEFS, getEvolutionCost } from '@/components/ComponentDefs';
import { getEvolutionResourceCost } from '@/config/ComponentEvolution';
import { SlotManager, SavedDesign } from '@/systems/SlotManager';
import { ResourceManager, ResourceState } from '@/systems/ResourceManager';
import { GOD } from '@/config/GodMode';
import { BaseUpgradeAxis } from '@/config/GameConfig';

const SNAPSHOT_KEY = 'robogame_pregame_snapshot';

export interface VictoryReward {
  baseReward: number;
  rankBonus: number;
  totalReward: number;
  newRankTier: number;
  prevRankTier: number;
  bonusCo: number;
  bonusElectro: number;
  /** Rerun count at time of calculation (before completeLevel increments it) */
  reruns: number;
}

interface PreGameSnapshot {
  slotIndex: number;
  progression: ProgressionState;
  designs: SavedDesign[];
  resources?: ResourceState;
}

// Starting components available in campaign without purchase
const STARTING_COMPONENTS = new Set<ComponentType>([
  ComponentType.STRUCTURE,
  ComponentType.BATTERY,
  ComponentType.SENSOR_SHORT,
  ComponentType.CANNON_LIGHT,
  ComponentType.DIRECTION,  // free utility
]);

// Workshop grid starts at 3×3, player can buy extra rows/cols ($250 each)
export const GRID_BASE_SIZE = 3;
export const GRID_MAX_SIZE = 5;
// Non-linear cost: each expansion costs more than the previous
// 3→4 = $2000, 4→5 = $8000
export const GRID_EXPAND_COSTS = [2000, 8000];

// ── Global grid size persistence (survives slot switches & resets) ──
const GRID_GLOBAL_KEY = 'robogame_grid_max';

function loadGlobalGridMax(): { rows: number; cols: number } {
  try {
    const raw = localStorage.getItem(GRID_GLOBAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        rows: Math.max(GRID_BASE_SIZE, Math.min(GRID_MAX_SIZE, parsed.rows ?? GRID_BASE_SIZE)),
        cols: Math.max(GRID_BASE_SIZE, Math.min(GRID_MAX_SIZE, parsed.cols ?? GRID_BASE_SIZE)),
      };
    }
  } catch { /* ignore */ }
  return { rows: GRID_BASE_SIZE, cols: GRID_BASE_SIZE };
}

function saveGlobalGridMax(rows: number, cols: number): void {
  const prev = loadGlobalGridMax();
  const maxRows = Math.max(prev.rows, rows);
  const maxCols = Math.max(prev.cols, cols);
  try {
    localStorage.setItem(GRID_GLOBAL_KEY, JSON.stringify({ rows: maxRows, cols: maxCols }));
  } catch { /* ignore */ }
}

export interface ProgressionState {
  currency: number;
  unlockedIds: string[];
  completedIds: string[];
  starsPerLevel: Record<string, number>;
  unlockedComponents: string[];          // ComponentType values
  componentLevels: Record<string, number>; // ComponentType → evolution level
  gridRows: number;   // current workshop grid rows
  gridCols: number;   // current workshop grid cols
  baseUpgradeLevel: number; // persistent base HP upgrade level (legacy, kept for migration)
  baseUpgradeLevels?: Record<string, number>; // axis → level (new multi-axis system)
  killsByType: Record<string, number>;   // EnemyType → cumulative kill count across campaign
  killsByTypeGrade?: Record<string, number>; // "type:grade" → cumulative kill count
  rerunsPerLevel?: Record<string, number>;   // levelId → number of completed runs (0 = never won)
  rankBonusClaimed?: Record<string, number>; // levelId → highest rank tier for which bonus was claimed (0=none,1=bronze,2=silver,3=gold,4=platinum)
  cumulativeStats?: CumulativeStats;
  achievementState?: AchievementPersistState;
}

/** Cumulative mission stats — accumulated across all missions for achievements */
export interface CumulativeStats {
  fogExplored: number;
  robotsDeployed: number;
  robotsLost: number;
  speedrunWins: number;    // maps completed in under 2 minutes (wave time only)
  minimalistWins: number;  // maps won with exactly 1 robot
  shotsHit: number;
  shotsFired: number;
  basesDestroyed: number;
  perfectWaves: number;
  bestStreak: number;
  totalDistance: number;
  // ── Funny/weird achievement stats ──
  winsAllRobotsDead: number;   // "Mission Accomplished...?" — won after all robots destroyed
  winsBaseUnder5Pct: number;   // "Sweating Bullets" — won with base HP < 5%
  winsLost10Plus: number;      // "Expendable" — won with 10+ robots lost
  winsNoDamage: number;        // "Untouchable" — won without base taking any damage
  winsOnlyFixed: number;       // "Immovable Object" — won with only stab-legs robots
  lostWave1: number;           // "Speedrun (Gone Wrong)" — lost on wave 1
  winsWave1: number;           // "SpeedRun (WRyyyyy)" — completed a map in under 30s (wave time)
  winsUnder10s: number;        // "Faster than light" — won in < 10 seconds
  maxRobotsLostOneMission: number; // "They're Just Numbers" — max robots lost in single mission
  afkLosses: number;           // "AFK" — lost without deploying any robot
  winsSupportOnly: number;     // "I'm Helping!" — deployed a pure support robot (no weapons) and won
  winsNoFlying: number;        // "Grounded" — won without any flying robot
  winsOnlyFlying: number;      // "Fly me to the Moon" — won with only flying robots
  mineKills: number;           // "Boom Surprise" — enemies killed by mines
  camperBaseKills: number;     // "Camper" — enemy bases destroyed by fixed robots
  cheatedWins: number;         // "Cheaters Never..." — won with a cheat active
  // ── New achievement stats ──
  maxDamageSurvived: number;      // biggest single hit a robot survived
  maxSingleHitDamage: number;     // biggest single hit dealt to an enemy
  maxCombosOneWave: number;       // most combos (3+ streaks) in a single wave
  totalRicochets: number;         // lifetime ricochet count (armor)
  maxPrecisionMission: number;    // best mission precision (0-100%)
}

/** Persisted achievement progress */
export interface AchievementPersistState {
  unlockedTiers: Record<string, number>;   // achievementId → highest tier index (1=bronze..4=platinum)
  claimedRewards: Record<string, number>;  // achievementId → highest tier reward claimed
  unlockedPerks: string[];                 // perkId list (from achievement rewards)
  seenPerks: string[];                     // perkIds the player has viewed in the Collection tab
}

/**
 * Singleton managing player progression (currency, unlocked/completed levels).
 * Persists to localStorage.
 */
class ProgressionManagerClass {
  private currency = 0;
  private unlocked = new Set<string>();
  private completed = new Set<string>();
  private stars = new Map<string, number>();
  private unlockedComponents = new Set<ComponentType>();
  private componentLevels = new Map<ComponentType, number>();
  private _gridRows = GRID_BASE_SIZE;
  private _gridCols = GRID_BASE_SIZE;
  private _baseUpgradeLevel = 0;
  private _baseUpgradeLevels = new Map<string, number>(); // BaseUpgradeAxis → level
  private _killsByType = new Map<string, number>();
  private _killsByTypeGrade = new Map<string, number>();
  private _rerunsPerLevel = new Map<string, number>();
  private _rankBonusClaimed = new Map<string, number>(); // 0=none, 1=bronze, 2=silver, 3=gold, 4=platinum
  private _cumulativeStats: CumulativeStats = { fogExplored: 0, robotsDeployed: 0, robotsLost: 0, speedrunWins: 0, minimalistWins: 0, shotsHit: 0, shotsFired: 0, basesDestroyed: 0, perfectWaves: 0, bestStreak: 0, totalDistance: 0, winsAllRobotsDead: 0, winsBaseUnder5Pct: 0, winsLost10Plus: 0, winsNoDamage: 0, winsOnlyFixed: 0, lostWave1: 0, winsWave1: 0, winsUnder10s: 0, maxRobotsLostOneMission: 0, afkLosses: 0, winsSupportOnly: 0, winsNoFlying: 0, winsOnlyFlying: 0, mineKills: 0, camperBaseKills: 0, cheatedWins: 0, maxDamageSurvived: 0, maxSingleHitDamage: 0, maxCombosOneWave: 0, totalRicochets: 0, maxPrecisionMission: 0 };
  private _achievementState: AchievementPersistState = { unlockedTiers: {}, claimedRewards: {}, unlockedPerks: [], seenPerks: [] };

  constructor() {
    this.load();
    // Ensure first level is always unlocked
    if (LEVEL_TREE.length > 0) {
      this.unlocked.add(LEVEL_TREE[0].id);
    }
    // Ensure starting components are always unlocked
    for (const ct of STARTING_COMPONENTS) {
      this.unlockedComponents.add(ct);
    }
  }

  // ── Currency ──

  getCurrency(): number { return this.currency; }

  addCurrency(amount: number): void {
    this.currency += Math.floor(amount);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
  }

  /** Spend campaign currency. Returns false if insufficient. */
  spendCurrency(amount: number): boolean {
    if (this.currency < amount) return false;
    this.currency -= Math.floor(amount);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    return true;
  }

  // ── Unlock ──

  isUnlocked(id: string): boolean { return this.unlocked.has(id); }
  isCompleted(id: string): boolean { return this.completed.has(id); }
  getStars(id: string): number { return this.stars.get(id) ?? 0; }
  /** Highest rank tier achieved: 0=none, 1=bronze, 2=silver, 3=gold, 4=platinum */
  getRankTier(id: string): number { return this._rankBonusClaimed.get(id) ?? 0; }

  /** Can this level be unlocked? (prereqs completed + enough currency + within demo cap) */
  canUnlock(id: string): boolean {
    if (this.unlocked.has(id)) return false;
    const node = LEVEL_MAP.get(id);
    if (!node) return false;
    // Demo cap: levels beyond cap are locked in non-DEV mode
    if (isDemoCapped(node) && !isDevMode()) return false;
    // At least one prereq must be completed
    if (node.prereqs.length > 0 && !node.prereqs.some(p => this.completed.has(p))) return false;
    return this.currency >= node.unlockCost;
  }

  /** Attempt to unlock a level (spend currency). Returns true if successful. */
  tryUnlock(id: string): boolean {
    if (!this.canUnlock(id)) return false;
    const node = LEVEL_MAP.get(id)!;
    this.currency -= node.unlockCost;
    this.unlocked.add(id);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    EventBus.emit(GameEvents.LEVEL_UNLOCKED, id);
    return true;
  }

  /** Mark a level as completed with a star rating.
   *  Increments rerun counter for this level. */
  completeLevel(id: string, starRating: number): void {
    this.completed.add(id);
    const prevStars = this.stars.get(id) ?? 0;
    if (starRating > prevStars) this.stars.set(id, starRating);
    // Increment rerun counter (first win = 0 reruns, second win = 1, etc.)
    const prevRuns = this._rerunsPerLevel.get(id) ?? 0;
    this._rerunsPerLevel.set(id, prevRuns + 1);
    this.save();
    EventBus.emit(GameEvents.LEVEL_COMPLETED, id);
  }

  /** Get the rerun count for a level (0 = never completed, 1 = completed once, etc.) */
  getReruns(id: string): number {
    return this._rerunsPerLevel.get(id) ?? 0;
  }

  /**
   * Calculate victory rewards for a completed level.
   * Returns { baseReward, rankBonus, totalReward, newRankTier }.
   *
   * Call BEFORE completeLevel() so rerun count reflects previous completions.
   *
   * Rank tiers: 0=none, 1=bronze(1★), 2=silver(2★), 3=gold(3★), 4=platinum
   * Rank bonuses (first-time per tier): silver +30, gold +60, platinum +100
   * Base reward: stars*20+10, degressive on reruns: base / (1 + 0.3 * reruns)
   */
  calculateVictoryReward(levelId: string, stars: number, isPlatinum: boolean): VictoryReward {
    // Current rank tier for this completion
    const currentTier = isPlatinum ? 4 : stars; // 0-4
    const prevClaimedTier = this._rankBonusClaimed.get(levelId) ?? 0;

    // Rank bonus amounts per tier (first-time only)
    //                        IRON  BRONZE  SILVER  GOLD  PLATINUM
    const RANK_CURRENCY:  Record<number, number> = { 1: 0, 2: 30,  3: 60,  4: 100 };
    const RANK_CO:        Record<number, number> = { 1: 2, 2: 5,   3: 10,  4: 20  };
    const RANK_ELECTRO:   Record<number, number> = { 1: 2, 2: 5,   3: 10,  4: 20  };

    // Sum bonuses for newly reached tiers only
    let rankBonus = 0;
    let bonusCo = 0;
    let bonusElectro = 0;
    for (let tier = prevClaimedTier + 1; tier <= currentTier; tier++) {
      rankBonus += RANK_CURRENCY[tier] ?? 0;
      bonusCo += RANK_CO[tier] ?? 0;
      bonusElectro += RANK_ELECTRO[tier] ?? 0;
    }

    // Update claimed tier if we reached a new high
    if (currentTier > prevClaimedTier) {
      this._rankBonusClaimed.set(levelId, currentTier);
    }

    // Base reward with degressive rerun penalty
    const reruns = this._rerunsPerLevel.get(levelId) ?? 0; // runs BEFORE this one
    const rawBase = stars * 20 + 10;
    const baseReward = Math.round(rawBase / (1 + 0.3 * reruns));

    return {
      baseReward,
      rankBonus,
      totalReward: baseReward + rankBonus,
      newRankTier: currentTier,
      prevRankTier: prevClaimedTier,
      bonusCo,
      bonusElectro,
      reruns,
    };
  }

  /** Get all levels that can currently be unlocked */
  getUnlockableIds(): string[] {
    return LEVEL_TREE
      .filter(n => !this.unlocked.has(n.id) && this.canUnlock(n.id))
      .map(n => n.id);
  }

  // ── Component unlocking (campaign mode) ──

  isComponentUnlocked(type: ComponentType): boolean {
    if (GOD.unlockAll) return true;
    return this.unlockedComponents.has(type);
  }

  canUnlockComponent(type: ComponentType): boolean {
    if (this.unlockedComponents.has(type)) return false; // already unlocked
    return this.meetsUnlockRequirements(type);
  }

  /** Does the player meet ALL unlock requirements for this component? */
  meetsUnlockRequirements(type: ComponentType): boolean {
    if (GOD.unlockAll) return true;
    if (this.unlockedComponents.has(type)) return true;
    const def = COMPONENT_DEFS[type];
    const req = def.unlockRequirements;
    // Currency check: use unlockRequirements.currency if defined, else fallback to unlockCost
    if (!GOD.freeBuilding) {
      const cost = Math.round((req?.currency ?? def.unlockCost) * GOD.workshopPriceMult);
      if (cost > 0 && this.currency < cost) return false;
    }
    // Future: check req.resources, req.storyFlag, req.minKills, etc.
    return true;
  }

  /** Should this component appear in the workshop palette?
   *  Unlocked → yes | Locked + meets requirements → yes (shows lock icon) | Otherwise → hidden */
  isComponentVisible(type: ComponentType): boolean {
    if (GOD.unlockAll) return true;
    if (this.unlockedComponents.has(type)) return true;
    return this.meetsUnlockRequirements(type);
  }

  tryUnlockComponent(type: ComponentType): boolean {
    if (!this.canUnlockComponent(type)) return false;
    const def = COMPONENT_DEFS[type];
    if (!GOD.freeBuilding) {
      const cost = Math.round(def.unlockCost * GOD.workshopPriceMult);
      if (this.currency < cost) return false;
      this.currency -= cost;
    }
    this.unlockedComponents.add(type);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    EventBus.emit(GameEvents.COMPONENT_UNLOCKED, type);
    return true;
  }

  getUnlockedComponents(): Set<ComponentType> {
    return new Set(this.unlockedComponents);
  }

  // ── Workshop grid size ──

  get gridRows(): number { return this._gridRows; }
  get gridCols(): number { return this._gridCols; }

  getExpandRowCost(): number {
    const idx = this._gridRows - GRID_BASE_SIZE;
    return GRID_EXPAND_COSTS[idx] ?? Infinity;
  }

  getExpandColCost(): number {
    const idx = this._gridCols - GRID_BASE_SIZE;
    return GRID_EXPAND_COSTS[idx] ?? Infinity;
  }

  canExpandRows(): boolean {
    return this._gridRows < GRID_MAX_SIZE && (GOD.freeBuilding || this.currency >= this.getExpandRowCost());
  }

  canExpandCols(): boolean {
    return this._gridCols < GRID_MAX_SIZE && (GOD.freeBuilding || this.currency >= this.getExpandColCost());
  }

  tryExpandRows(): boolean {
    if (this._gridRows >= GRID_MAX_SIZE) return false;
    const cost = this.getExpandRowCost();
    if (!GOD.freeBuilding) {
      if (this.currency < cost) return false;
      this.currency -= cost;
    }
    this._gridRows += 1;
    saveGlobalGridMax(this._gridRows, this._gridCols);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    EventBus.emit(GameEvents.GRID_EXPANDED, { rows: this._gridRows, cols: this._gridCols });
    return true;
  }

  tryExpandCols(): boolean {
    if (this._gridCols >= GRID_MAX_SIZE) return false;
    const cost = this.getExpandColCost();
    if (!GOD.freeBuilding) {
      if (this.currency < cost) return false;
      this.currency -= cost;
    }
    this._gridCols += 1;
    saveGlobalGridMax(this._gridRows, this._gridCols);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    EventBus.emit(GameEvents.GRID_EXPANDED, { rows: this._gridRows, cols: this._gridCols });
    return true;
  }

  // ── Base upgrade ──

  /** @deprecated Use getBaseAxisLevel / setBaseAxisLevel instead */
  get baseUpgradeLevel(): number { return this._baseUpgradeLevel; }

  /** @deprecated Kept for legacy compat — prefer setBaseAxisLevel */
  setBaseUpgradeLevel(level: number): void {
    this._baseUpgradeLevel = level;
    this.save();
    EventBus.emit(GameEvents.BASE_UPGRADE_CHANGED, level);
  }

  // ── Multi-axis base upgrades ──

  getBaseAxisLevel(axis: BaseUpgradeAxis): number {
    return this._baseUpgradeLevels.get(axis) ?? 0;
  }

  setBaseAxisLevel(axis: BaseUpgradeAxis, level: number): void {
    this._baseUpgradeLevels.set(axis, level);
    // Keep legacy HP level in sync for backward compat
    if (axis === BaseUpgradeAxis.HP) this._baseUpgradeLevel = level;
    this.save();
    EventBus.emit(GameEvents.BASE_UPGRADE_CHANGED, { axis, level });
  }

  getBaseUpgradeLevels(): Record<string, number> {
    return Object.fromEntries(this._baseUpgradeLevels);
  }

  // ── Kill tracking ──

  /** Add kills from a completed mission (called once at game-over).
   *  killsByType: EnemyType → count (legacy)
   *  killsByTypeGrade: "type:grade" → count (detailed) */
  addKills(kills: Record<string, number>, killsByGrade?: Record<string, number>): void {
    for (const [type, count] of Object.entries(kills)) {
      this._killsByType.set(type, (this._killsByType.get(type) ?? 0) + count);
    }
    if (killsByGrade) {
      for (const [key, count] of Object.entries(killsByGrade)) {
        this._killsByTypeGrade.set(key, (this._killsByTypeGrade.get(key) ?? 0) + count);
      }
    }
    this.save();
    EventBus.emit(GameEvents.KILLS_UPDATED, Object.fromEntries(this._killsByType));
  }

  getKillsByType(): Record<string, number> {
    return Object.fromEntries(this._killsByType);
  }

  getKillsByTypeGrade(): Record<string, number> {
    return Object.fromEntries(this._killsByTypeGrade);
  }

  getTotalKills(): number {
    let total = 0;
    for (const count of this._killsByType.values()) total += count;
    return total;
  }

  // ── Cumulative mission stats (for achievements) ──

  /** Accumulate per-mission stats into lifetime counters. Called from GameScene.onGameOver. */
  addMissionStats(mission: {
    fogExplored: number; robotsDeployed: number; robotsLost: number;
    shotsHit: number; shotsFired: number; basesDestroyed: number;
    perfectWaves: number; bestStreak: number; totalDistance: number;
    won: boolean; wavesReached: number;
    // ── Extra fields for funny achievements ──
    hpRatio: number;            // base HP ratio at end (0..1)
    elapsedMs: number;          // mission duration in ms
    allRobotsDead: boolean;     // robotsLost >= robotsDeployed && deployed > 0
    onlyFixedRobots: boolean;   // all deployed robots had stab legs / no movement
    noFlyingRobots: boolean;    // no flying robot was deployed
    onlyFlyingRobots: boolean;  // all deployed robots could fly
    hadSupportOnlyBot: boolean; // at least one robot had zero weapons
    mineKills: number;          // enemies killed by mines this mission
    camperBaseKills: number;    // enemy bases destroyed by fixed robots
    usedCheat: boolean;         // any GOD cheat was active during this mission
    maxDamageSurvived: number;
    maxSingleHitDamage: number;
    maxCombosOneWave: number;
    totalRicochets: number;
    shotsHitPrecision: number;  // precision % (0-100) for this mission
  }): void {
    const s = this._cumulativeStats;
    s.fogExplored += mission.fogExplored;
    s.robotsDeployed += mission.robotsDeployed;
    s.robotsLost += mission.robotsLost;
    s.shotsHit += mission.shotsHit;
    s.shotsFired += mission.shotsFired;
    s.basesDestroyed += mission.basesDestroyed;
    s.perfectWaves += mission.perfectWaves;
    s.bestStreak = Math.max(s.bestStreak, mission.bestStreak);
    s.totalDistance += mission.totalDistance;
    s.mineKills += mission.mineKills;
    s.camperBaseKills += mission.camperBaseKills;
    s.maxRobotsLostOneMission = Math.max(s.maxRobotsLostOneMission, mission.robotsLost);
    if (mission.won && mission.usedCheat) s.cheatedWins++;
    s.maxDamageSurvived = Math.max(s.maxDamageSurvived, mission.maxDamageSurvived);
    s.maxSingleHitDamage = Math.max(s.maxSingleHitDamage, mission.maxSingleHitDamage);
    s.maxCombosOneWave = Math.max(s.maxCombosOneWave, mission.maxCombosOneWave);
    s.totalRicochets += mission.totalRicochets;
    s.maxPrecisionMission = Math.max(s.maxPrecisionMission, mission.shotsHitPrecision);

    if (mission.won) {
      if (mission.elapsedMs < 120000) s.speedrunWins++; // under 2 min (wave time only)
      if (mission.robotsDeployed === 1) s.minimalistWins++;
      if (mission.allRobotsDead) s.winsAllRobotsDead++;
      if (mission.hpRatio > 0 && mission.hpRatio < 0.05) s.winsBaseUnder5Pct++;
      if (mission.robotsLost >= 10) s.winsLost10Plus++;
      if (mission.hpRatio >= 1.0) s.winsNoDamage++;
      if (mission.onlyFixedRobots && mission.robotsDeployed > 0) s.winsOnlyFixed++;
      if (mission.elapsedMs < 30000) s.winsWave1++; // under 30s (wave time only)
      if (mission.elapsedMs < 10000) s.winsUnder10s++;
      if (mission.hadSupportOnlyBot) s.winsSupportOnly++;
      if (mission.noFlyingRobots && mission.robotsDeployed > 0) s.winsNoFlying++;
      if (mission.onlyFlyingRobots && mission.robotsDeployed > 0) s.winsOnlyFlying++;
    } else {
      if (mission.wavesReached <= 1) s.lostWave1++;
      if (mission.robotsDeployed === 0) s.afkLosses++;
    }
    this.save();
  }

  getCumulativeStats(): CumulativeStats { return { ...this._cumulativeStats }; }

  // ── Achievement state ──

  getAchievementTier(id: string): number {
    return this._achievementState.unlockedTiers[id] ?? 0;
  }

  setAchievementTier(id: string, tier: number): void {
    this._achievementState.unlockedTiers[id] = tier;
    this.save();
  }

  getAchievementClaimedReward(id: string): number {
    return this._achievementState.claimedRewards[id] ?? 0;
  }

  setAchievementClaimedReward(id: string, tier: number): void {
    this._achievementState.claimedRewards[id] = tier;
    this.save();
  }

  getAchievementState(): AchievementPersistState {
    return {
      unlockedTiers: { ...this._achievementState.unlockedTiers },
      claimedRewards: { ...this._achievementState.claimedRewards },
      unlockedPerks: [...this._achievementState.unlockedPerks],
      seenPerks: [...this._achievementState.seenPerks],
    };
  }

  /** Sum of all unlocked tier values (for meta-achievement) */
  getTotalAchievementTiers(): number {
    let sum = 0;
    for (const v of Object.values(this._achievementState.unlockedTiers)) sum += v;
    return sum;
  }

  // ── Perks ──

  unlockPerk(perkId: string): void {
    if (!this._achievementState.unlockedPerks.includes(perkId)) {
      this._achievementState.unlockedPerks.push(perkId);
      this.save();
    }
  }

  hasUnlockedPerk(perkId: string): boolean {
    return this._achievementState.unlockedPerks.includes(perkId);
  }

  getUnlockedPerks(): string[] {
    return [...this._achievementState.unlockedPerks];
  }

  /** Count of unlocked perks that haven't been seen in the Collection tab */
  getUnseenPerkCount(): number {
    const seen = new Set(this._achievementState.seenPerks);
    return this._achievementState.unlockedPerks.filter(p => !seen.has(p)).length;
  }

  /** Mark all currently unlocked perks as seen (called when Collection tab opens) */
  markAllPerksSeen(): void {
    const seen = new Set(this._achievementState.seenPerks);
    for (const p of this._achievementState.unlockedPerks) {
      if (!seen.has(p)) this._achievementState.seenPerks.push(p);
    }
    this.save();
  }

  /** Count of completed levels */
  getCompletedCount(): number { return this.completed.size; }

  /** Count of unlocked levels */
  getUnlockedCount(): number { return this.unlocked.size; }

  /** Sum of all stars across levels */
  getTotalStars(): number {
    let sum = 0;
    for (const v of this.stars.values()) sum += v;
    return sum;
  }

  /** Sum of all rerun counts */
  getTotalReruns(): number {
    let sum = 0;
    for (const v of this._rerunsPerLevel.values()) sum += v;
    return sum;
  }

  /** Sum of all component evolution levels */
  getTotalEvolutions(): number {
    let sum = 0;
    for (const v of this.componentLevels.values()) sum += v;
    return sum;
  }

  /** Count of components at their maximum evolution level */
  getMaxEvoComponentCount(): number {
    let count = 0;
    for (const [type, level] of this.componentLevels.entries()) {
      const def = COMPONENT_DEFS[type];
      if (def && level >= def.maxEvolutionLevel) count++;
    }
    return count;
  }

  // ── Component evolution ──

  getComponentLevel(type: ComponentType): number {
    return this.componentLevels.get(type) ?? 0;
  }

  canEvolveComponent(type: ComponentType): boolean {
    const def = COMPONENT_DEFS[type];
    const currentLevel = this.getComponentLevel(type);
    if (currentLevel >= def.maxEvolutionLevel) return false;
    if (GOD.freeBuilding) return true;
    const cost = Math.round(getEvolutionCost(currentLevel, def.maxEvolutionLevel, type) * GOD.workshopPriceMult);
    if (this.currency < cost) return false;
    const res = getEvolutionResourceCost(type, currentLevel);
    if (ResourceManager.co < res.co || ResourceManager.electro < res.electro) return false;
    return true;
  }

  tryEvolveComponent(type: ComponentType): boolean {
    const def = COMPONENT_DEFS[type];
    const currentLevel = this.getComponentLevel(type);
    if (currentLevel >= def.maxEvolutionLevel) return false;
    if (!GOD.freeBuilding) {
      const cost = Math.round(getEvolutionCost(currentLevel, def.maxEvolutionLevel, type) * GOD.workshopPriceMult);
      const res = getEvolutionResourceCost(type, currentLevel);
      if (this.currency < cost) return false;
      if (ResourceManager.co < res.co || ResourceManager.electro < res.electro) return false;
      this.currency -= cost;
      if (res.co > 0) ResourceManager.spendCo(res.co);
      if (res.electro > 0) ResourceManager.spendElectro(res.electro);
    }
    this.componentLevels.set(type, currentLevel + 1);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
    EventBus.emit(GameEvents.COMPONENT_EVOLVED, { type, level: currentLevel + 1 });
    return true;
  }

  // ── Rollback (preserving permanent progression) ──

  /** Rollback to a snapshot but keep unlocks & evolutions acquired since.
   *  Currency is restored minus the cost of any new unlocks/evolutions. */
  rollbackToState(snapshot: ProgressionState): void {
    // Capture current permanent progression before overwriting
    const currentUnlocks = new Set(this.unlockedComponents);
    const currentLevels = new Map(this.componentLevels);

    // Full restore from snapshot
    this.loadFromState(snapshot);

    // Re-apply unlocks that happened after the snapshot
    for (const type of currentUnlocks) {
      if (!this.unlockedComponents.has(type)) {
        this.unlockedComponents.add(type);
        // Deduct the unlock cost from restored budget
        const def = COMPONENT_DEFS[type];
        if (!GOD.freeBuilding) {
          const cost = Math.round(def.unlockCost * GOD.workshopPriceMult);
          this.currency = Math.max(0, this.currency - cost);
        }
      }
    }

    // Re-apply evolution levels that happened after the snapshot
    for (const [type, currentLvl] of currentLevels) {
      const snapshotLvl = this.componentLevels.get(type) ?? 0;
      if (currentLvl > snapshotLvl) {
        this.componentLevels.set(type, currentLvl);
        // Deduct evolution costs for each level gained
        if (!GOD.freeBuilding) {
          for (let lvl = snapshotLvl; lvl < currentLvl; lvl++) {
            const cost = Math.round(getEvolutionCost(lvl, COMPONENT_DEFS[type].maxEvolutionLevel, type) * GOD.workshopPriceMult);
            this.currency = Math.max(0, this.currency - cost);
          }
        }
      }
    }

    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
  }

  // ── Persistence ──

  /** Get current state as a serializable object */
  getState(): ProgressionState {
    return {
      currency: this.currency,
      unlockedIds: [...this.unlocked],
      completedIds: [...this.completed],
      starsPerLevel: Object.fromEntries(this.stars),
      unlockedComponents: [...this.unlockedComponents],
      componentLevels: Object.fromEntries(this.componentLevels),
      gridRows: this._gridRows,
      gridCols: this._gridCols,
      baseUpgradeLevel: this._baseUpgradeLevel,
      baseUpgradeLevels: Object.fromEntries(this._baseUpgradeLevels),
      killsByType: Object.fromEntries(this._killsByType),
      killsByTypeGrade: Object.fromEntries(this._killsByTypeGrade),
      rerunsPerLevel: Object.fromEntries(this._rerunsPerLevel),
      rankBonusClaimed: Object.fromEntries(this._rankBonusClaimed),
      cumulativeStats: { ...this._cumulativeStats },
      achievementState: this.getAchievementState(),
    };
  }

  /** Load state from a given ProgressionState object (used when loading a slot) */
  loadFromState(state: ProgressionState): void {
    this.currency = state.currency ?? 0;
    this.unlocked = new Set(state.unlockedIds ?? []);
    this.completed = new Set(state.completedIds ?? []);
    this.stars = new Map(Object.entries(state.starsPerLevel ?? {}));
    this.unlockedComponents = new Set((state.unlockedComponents ?? []) as ComponentType[]);
    this.componentLevels = new Map(
      Object.entries(state.componentLevels ?? {}) as [ComponentType, number][],
    );
    // Grid size: take the max between slot value and global persistent max (never regresses)
    const globalGrid = loadGlobalGridMax();
    this._gridRows = Math.max(globalGrid.rows, Math.max(GRID_BASE_SIZE, Math.min(GRID_MAX_SIZE, state.gridRows ?? GRID_BASE_SIZE)));
    this._gridCols = Math.max(globalGrid.cols, Math.max(GRID_BASE_SIZE, Math.min(GRID_MAX_SIZE, state.gridCols ?? GRID_BASE_SIZE)));
    this._baseUpgradeLevel = state.baseUpgradeLevel ?? 0;
    // Multi-axis base upgrades — migrate from legacy single HP level if needed
    if (state.baseUpgradeLevels && Object.keys(state.baseUpgradeLevels).length > 0) {
      this._baseUpgradeLevels = new Map(Object.entries(state.baseUpgradeLevels).map(([k, v]) => [k, v as number]));
    } else {
      this._baseUpgradeLevels = new Map();
      if (this._baseUpgradeLevel > 0) {
        this._baseUpgradeLevels.set(BaseUpgradeAxis.HP, this._baseUpgradeLevel);
      }
    }
    this._killsByType = new Map(Object.entries(state.killsByType ?? {}));
    this._killsByTypeGrade = new Map(Object.entries(state.killsByTypeGrade ?? {}));
    this._rerunsPerLevel = new Map(Object.entries(state.rerunsPerLevel ?? {}).map(([k, v]) => [k, v as number]));
    this._rankBonusClaimed = new Map(Object.entries(state.rankBonusClaimed ?? {}).map(([k, v]) => [k, v as number]));
    const cs = state.cumulativeStats;
    const csDefaults: CumulativeStats = { fogExplored: 0, robotsDeployed: 0, robotsLost: 0, speedrunWins: 0, minimalistWins: 0, shotsHit: 0, shotsFired: 0, basesDestroyed: 0, perfectWaves: 0, bestStreak: 0, totalDistance: 0, winsAllRobotsDead: 0, winsBaseUnder5Pct: 0, winsLost10Plus: 0, winsNoDamage: 0, winsOnlyFixed: 0, lostWave1: 0, winsWave1: 0, winsUnder10s: 0, maxRobotsLostOneMission: 0, afkLosses: 0, winsSupportOnly: 0, winsNoFlying: 0, winsOnlyFlying: 0, mineKills: 0, camperBaseKills: 0, cheatedWins: 0, maxDamageSurvived: 0, maxSingleHitDamage: 0, maxCombosOneWave: 0, totalRicochets: 0, maxPrecisionMission: 0 };
    this._cumulativeStats = cs ? { ...csDefaults, ...cs } : { ...csDefaults };
    const as2 = state.achievementState;
    this._achievementState = as2 ? { unlockedTiers: { ...as2.unlockedTiers }, claimedRewards: { ...as2.claimedRewards }, unlockedPerks: [...(as2.unlockedPerks ?? [])], seenPerks: [...(as2.seenPerks ?? [])] } : { unlockedTiers: {}, claimedRewards: {}, unlockedPerks: [], seenPerks: [] };
    // Ensure starting components + first level
    for (const ct of STARTING_COMPONENTS) this.unlockedComponents.add(ct);
    if (LEVEL_TREE.length > 0) this.unlocked.add(LEVEL_TREE[0].id);
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
  }

  // ── Pre-game snapshot (crash recovery) ──

  /** Save a snapshot of current state before entering a campaign level.
   *  If the game crashes/reloads, this snapshot will be restored on next boot. */
  savePreGameSnapshot(): void {
    const slotIdx = SlotManager.activeSlot;
    if (slotIdx < 0) return;
    const slotData = SlotManager.readSlot(slotIdx);
    const snapshot: PreGameSnapshot = {
      slotIndex: slotIdx,
      progression: this.getState(),
      designs: slotData?.designs ?? [],
      resources: ResourceManager.getState(),
    };
    try {
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (e) { console.warn('[ProgressionManager] savePreGameSnapshot failed:', e); }
  }

  /** Clear the snapshot (called on normal game end: victory or defeat). */
  clearPreGameSnapshot(): void {
    try { localStorage.removeItem(SNAPSHOT_KEY); } catch (e) { console.warn('[ProgressionManager] clearPreGameSnapshot failed:', e); }
  }

  /** If a snapshot exists for the active slot, restore it (crash recovery).
   *  Returns true if a restore happened. */
  private restoreIfCrashed(): boolean {
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY);
      if (!raw) return false;
      const snapshot: PreGameSnapshot = JSON.parse(raw);
      // Only restore if same slot is active (avoid cross-slot contamination)
      if (snapshot.slotIndex !== SlotManager.activeSlot) {
        localStorage.removeItem(SNAPSHOT_KEY);
        return false;
      }
      // Restore progression (preserve permanent unlocks & evolutions)
      this.rollbackToState(snapshot.progression);
      // Restore Co/Electro
      if (snapshot.resources) {
        ResourceManager.loadFromState(snapshot.resources);
        ResourceManager.save();
      }
      // Restore designs
      SlotManager.updateDesigns(snapshot.designs);
      // Clear snapshot so it doesn't trigger again
      localStorage.removeItem(SNAPSHOT_KEY);
      return true;
    } catch (e) {
      console.warn('[ProgressionManager] restoreIfCrashed failed:', e);
      localStorage.removeItem(SNAPSHOT_KEY);
      return false;
    }
  }

  /** Save to active slot via SlotManager */
  save(): void {
    SlotManager.updateProgression(this.getState());
  }

  private load(): void {
    // Migrate old single-save if needed
    SlotManager.migrateOldSave();

    const slotIdx = SlotManager.activeSlot;
    if (slotIdx < 0) return;

    // Load slot data FIRST so crash recovery can see current unlocks/evolutions.
    // Without this, rollbackToState captures an empty currentUnlocks set and
    // loses any unlocks that happened after the pre-game snapshot was taken.
    const slotData = SlotManager.readSlot(slotIdx);
    if (slotData) {
      this.loadFromState(slotData.progression);
    }

    // Now try crash recovery — rollbackToState will preserve the unlocks
    // loaded above even if the snapshot predates them.
    if (this.restoreIfCrashed()) return;
  }

  /** Reset all progression (for new game) */
  reset(): void {
    this.currency = GOD.startingMoney;
    this.unlocked = new Set();
    this.completed = new Set();
    this.stars = new Map();
    this.unlockedComponents = new Set(STARTING_COMPONENTS);
    this.componentLevels = new Map();
    // Grid size persists globally — never reset
    const globalGrid = loadGlobalGridMax();
    this._gridRows = globalGrid.rows;
    this._gridCols = globalGrid.cols;
    this._baseUpgradeLevel = 0;
    this._baseUpgradeLevels = new Map();
    this._killsByType = new Map();
    this._killsByTypeGrade = new Map();
    this._rerunsPerLevel = new Map();
    this._rankBonusClaimed = new Map();
    this._cumulativeStats = { fogExplored: 0, robotsDeployed: 0, robotsLost: 0, speedrunWins: 0, minimalistWins: 0, shotsHit: 0, shotsFired: 0, basesDestroyed: 0, perfectWaves: 0, bestStreak: 0, totalDistance: 0, winsAllRobotsDead: 0, winsBaseUnder5Pct: 0, winsLost10Plus: 0, winsNoDamage: 0, winsOnlyFixed: 0, lostWave1: 0, winsWave1: 0, winsUnder10s: 0, maxRobotsLostOneMission: 0, afkLosses: 0, winsSupportOnly: 0, winsNoFlying: 0, winsOnlyFlying: 0, mineKills: 0, camperBaseKills: 0, cheatedWins: 0, maxDamageSurvived: 0, maxSingleHitDamage: 0, maxCombosOneWave: 0, totalRicochets: 0, maxPrecisionMission: 0 };
    this._achievementState = { unlockedTiers: {}, claimedRewards: {}, unlockedPerks: [], seenPerks: [] };
    if (LEVEL_TREE.length > 0) this.unlocked.add(LEVEL_TREE[0].id);
    this.save();
    EventBus.emit(GameEvents.CURRENCY_CHANGED, this.currency);
  }
}

/** Singleton instance */
export const ProgressionManager = new ProgressionManagerClass();
